import React, { useEffect, useRef, useState } from 'react';
import { Rnd } from 'react-rnd';
import { Box, Paper, Group, Text, ActionIcon, useMantineTheme, Tooltip, Avatar, Portal } from '@mantine/core';
import { IconX, IconMinus, IconGauge } from '@tabler/icons';
import ChatComponent from './ChatComponent';
import BagelIcon from './BagelIcon';
import './chat.css';

const CHAT_GUTTER = 16;
const DEFAULT_CHAT_SIZE = { width: 400, height: 480 };

function bottomRightPosition(chatWidth: number, chatHeight: number) {
  if (typeof window === 'undefined') {
    return { x: CHAT_GUTTER, y: CHAT_GUTTER };
  }
  return {
    x: Math.max(CHAT_GUTTER, window.innerWidth - chatWidth - CHAT_GUTTER),
    y: Math.max(CHAT_GUTTER, window.innerHeight - chatHeight - CHAT_GUTTER),
  };
}

interface DraggableChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DraggableChatModal: React.FC<DraggableChatModalProps> = ({
  isOpen,
  onClose,
}) => {
  const theme = useMantineTheme();
  const [isMinimized, setIsMinimized] = useState(false);
  const [totalTokensUsed, setTotalTokensUsed] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPosition, setDragStartPosition] = useState({ x: 0, y: 0 });
  const [isCustomDragging, setIsCustomDragging] = useState(false);

  const [position, setPosition] = useState(() =>
    bottomRightPosition(DEFAULT_CHAT_SIZE.width, DEFAULT_CHAT_SIZE.height)
  );
  const [size, setSize] = useState(DEFAULT_CHAT_SIZE);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      const w = isMinimized ? 60 : size.width;
      const h = isMinimized ? 60 : size.height;
      setPosition(bottomRightPosition(w, h));
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, isMinimized, size.width, size.height]);

  if (!isOpen) return null;

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const handleTokenUsageChange = (tokens: number) => {
    setTotalTokensUsed(tokens);
  };

  const formatTokenUsage = (tokens: number) => {
    if (tokens === 0) return '0 tokens used';
    if (tokens < 1000) return `${tokens} tokens used`;
    return `${(tokens / 1000).toFixed(1)}K tokens used`;
  };

  // Precise drag handler for minimized widget
  const handlePreciseMouseDown = (e: React.MouseEvent) => {
    if (!isMinimized) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startPosX = position.x;
    const startPosY = position.y;
    
    setDragStartPosition({ x: startX, y: startY });
    setIsCustomDragging(true);
    
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      
      const newX = startPosX + deltaX;
      const newY = startPosY + deltaY;
      
      // Apply bounds checking
      const boundedX = Math.max(0, Math.min(window.innerWidth - 60, newX));
      const boundedY = Math.max(0, Math.min(window.innerHeight - 60, newY));
      
      setPosition({ x: boundedX, y: boundedY });
    };
    
    const handleMouseUp = (e: MouseEvent) => {
      setIsCustomDragging(false);
      
      // Check if we actually moved significantly
      const moved = Math.abs(e.clientX - startX) > 5 || Math.abs(e.clientY - startY) > 5;
      setIsDragging(moved);
      
      // Clear the isDragging flag after a short delay
      setTimeout(() => setIsDragging(false), 100);
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };


  return (
    <Portal>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          pointerEvents: 'none',
          zIndex: 9999,
        }}
      >
        <Rnd
          position={position}
          size={isMinimized ? { width: 60, height: 60 } : size}
          onDragStart={(e, d) => {
            setDragStartPosition({ x: d.x, y: d.y });
            setIsDragging(false); // Reset at start
          }}
          onDragStop={(e, d) => {
            setPosition({ x: d.x, y: d.y });
            // Check if we actually moved significantly (more than 5 pixels)
            const moved = Math.abs(d.x - dragStartPosition.x) > 5 || Math.abs(d.y - dragStartPosition.y) > 5;
            setIsDragging(moved);
            // Reset dragging state after a short delay
            setTimeout(() => setIsDragging(false), 100);
          }}
          onResizeStop={(e, direction, ref, delta, position) => {
            if (!isMinimized) {
              setSize({
                width: parseInt(ref.style.width),
                height: parseInt(ref.style.height),
              });
              setPosition(position);
            }
          }}
          minWidth={isMinimized ? 60 : 300}
          minHeight={isMinimized ? 60 : 400}
          maxWidth={isMinimized ? 60 : 800}
          maxHeight={isMinimized ? 60 : Math.min(900, typeof window !== 'undefined' ? window.innerHeight - CHAT_GUTTER * 2 : 900)}
          bounds="window"
          dragHandleClassName={isMinimized ? undefined : "chat-modal-drag-handle"}
          disableDragging={isMinimized}
          enableResizing={!isMinimized}
          dragAxis="both"
          style={{
            pointerEvents: 'auto',
          }}
        >
          {isMinimized ? (
          // Circular minimized widget
            <Box
              onMouseDown={handlePreciseMouseDown}
              onClick={() => {
                // Only expand if we're not in the middle of dragging
                if (!isDragging) {
                  handleMinimize();
                }
              }}
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                backgroundColor: '#FF8700', // Orange background to match bagel
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isCustomDragging ? 'grabbing' : 'grab',
                userSelect: 'none',
                '&:hover': {
                  transform: isCustomDragging ? 'none' : 'scale(1.05)',
                },
                transition: isCustomDragging ? 'none' : 'all 0.2s ease',
              }}
            >
              <BagelIcon height={48} width={48} />
            </Box>
          ) : (
          // Full chat modal
            <Paper
              shadow="xl"
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid ${theme.colors.gray[3]}`,
                borderRadius: theme.radius.md,
                overflow: 'hidden',
              }}
            >
              {/* Modal Header */}
              <Box
                className="chat-modal-drag-handle"
                sx={{
                  backgroundColor: '#5B60E3', // Same as MainHeader
                  color: 'white',
                  padding: theme.spacing.sm,
                  cursor: 'move',
                  userSelect: 'none',
                }}
              >
                <Group position="apart" align="center">
                  <Group spacing="sm" align="center">
                    <Avatar radius="xl" size="sm" sx={{ backgroundColor: 'transparent', padding: 0 }}>
                      <BagelIcon height={24} width={24} />
                    </Avatar>
                    <Text weight={500} size="sm">
                  Account Setup Agent
                    </Text>
                  </Group>
                  <Group spacing={4}>
                    {/* Token Usage Meter */}
                    <Tooltip label={formatTokenUsage(totalTokensUsed)} position="bottom">
                      <ActionIcon
                        size="sm"
                        variant="transparent"
                        sx={{
                          color: 'white',
                          cursor: 'default',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          },
                        }}
                      >
                        <IconGauge size={14} />
                      </ActionIcon>
                    </Tooltip>
                
                    <ActionIcon
                      size="sm"
                      color="blue"
                      variant="transparent"
                      onClick={handleMinimize}
                      sx={{
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      <IconMinus size={14} />
                    </ActionIcon>
                    <ActionIcon
                      size="sm"
                      color="blue"
                      variant="transparent"
                      onClick={onClose}
                      sx={{
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Box>

              {/* Chat Content */}
              <Box style={{ flex: 1, overflow: 'hidden' }}>
                <ChatComponent onTokenUsageChange={handleTokenUsageChange} />
              </Box>
            </Paper>
          )}
        </Rnd>
      </Box>
    </Portal>
  );
};

export default DraggableChatModal;
